import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Card,
    Chip,
    CircularProgress,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from "@mui/material";
import { HistoryRounded, ReceiptLongRounded } from "@mui/icons-material";
import { transaction_increaseQuery } from "../../network/api/transactions.api";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { useSelector } from "react-redux";
import { increaseCorporateDeposit_getInvoice } from "../../network/api/increaseCorporateDeposit.api";
import _IncreaseHistoryHeader from "./partials/_IncreaseHistoryHeader";
import _IncreaseHistoryTableRow from "./partials/_IncreaseHistoryTableRow";

const IncreaseHistory = () => {
    const error = useContext(ErrorContext);
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [transactions, setTransactions] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        getHistory();
    }, [page, rowsPerPage]);

    function getHistory() {
        if (!corporate?.Id) return;
        setTransactions(null);
        transaction_increaseQuery({
            queryType: "FILTER",
            CorporateId: corporate.Id,
            paging: { Page: page, Size: rowsPerPage, Desc: true }
        }).then((data) => {
            setTransactions(data.data.Data || {});
        }).catch(e => {
            setTransactions({ content: [] });
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در دریافت تاریخچه تراکنش‌ها" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    function getInvoice(e, item) {
        e.preventDefault();
        setDownloadingId(item.Id);
        increaseCorporateDeposit_getInvoice({
            Id: item.Id
        }).then((response) => {
            setDownloadingId(null);
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice-${item.Id}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }).catch(e => {
            setDownloadingId(null);
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در دانلود فاکتور" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    const isLoading = transactions === null;
    const historyList = transactions?.content || [];
    const totalCount = transactions?.totalElements || 0;

    return (
        <Container maxWidth="xl" sx={{ pt: 3, pb: 6 }}>
            <title>تاریخچه تراکنش‌ها و افزایش شارژ | جیم‌پین</title>

            {/* Header & Back Navigation */}
            <_IncreaseHistoryHeader />

            {/* Table Card */}
            <Card
                elevation={0}
                sx={{
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 14px rgba(0, 0, 0, 0.03)',
                    overflow: 'hidden'
                }}
            >
                {/* Header Toolbar */}
                <Box
                    sx={{
                        p: 2.2,
                        px: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <HistoryRounded sx={{ color: 'primary.main', fontSize: 22 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            لیست سوابق واریز و افزایش اعتبار
                        </Typography>
                    </Box>
                    <Chip
                        label={`مجموع تراکنش‌ها: ${totalCount}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 700, borderRadius: 2 }}
                    />
                </Box>

                <TableContainer>
                    <Table aria-label="تاریخچه تراکنش‌ها" sx={{ minWidth: 700 }}>
                        <TableHead>
                            <TableRow
                                sx={{
                                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                                }}
                            >
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    شناسه پیگیری
                                </TableCell>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    مبلغ واریز
                                </TableCell>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    وضعیت
                                </TableCell>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    تاریخ و زمان
                                </TableCell>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    ثبت‌کننده
                                </TableCell>
                                <TableCell align="left" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem', width: 140 }}>
                                    فاکتور
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={36} thickness={4} sx={{ color: 'primary.main', mb: 1.5 }} />
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                            در حال دریافت تاریخچه تراکنش‌ها...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : historyList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '50%',
                                                bgcolor: 'rgba(28, 139, 156, 0.08)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                mb: 2,
                                                color: 'primary.main'
                                            }}
                                        >
                                            <ReceiptLongRounded sx={{ fontSize: 32 }} />
                                        </Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
                                            هیچ تراکنشی یافت نشد
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360, mx: 'auto' }}>
                                            تاکنون هیچ درخواست افزایش شارژ یا تراکنش مالی برای این سازمان ثبت نشده است.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                historyList.map((row) => (
                                    <_IncreaseHistoryTableRow
                                        key={"tr-" + (row.Id || row.Serial)}
                                        row={row}
                                        downloadingId={downloadingId}
                                        onGetInvoice={getInvoice}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination Footer */}
                {totalCount > 0 && (
                    <Box
                        sx={{
                            p: 1.5,
                            px: 2.5,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)'
                        }}
                    >
                        <TablePagination
                            component="div"
                            rowsPerPageOptions={[10, 15, 30, 50]}
                            count={totalCount}
                            labelRowsPerPage="تعداد در هر صفحه:"
                            labelDisplayedRows={({ from, to, count }) => `نمایش ${from} تا ${to} از ${count} تراکنش`}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(parseInt(event.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </Box>
                )}
            </Card>
        </Container>
    );
};

export default IncreaseHistory;
